/**
 * Modul "Routenregeln": ein einzelner, formatierbarer Text (Fett/
 * Unterstrichen/normaler Text) für die Benzin-Route. Rechte-Modell (siehe
 * Migration 0053, Kategorie "Routenregeln"):
 *   - routenregeln.view      Ansehen - alle regulären Rollen + "Routenpartner".
 *   - routenregeln.manage    Bearbeiten/Speichern/Löschen - nur President/VizePresident.
 * Der Editor ist für Rollen ohne "routenregeln.manage" komplett schreib-
 * geschützt (weder Bearbeiten- noch Löschen-Button sichtbar) - zusätzlich
 * serverseitig über requirePermission() abgesichert (backend/src/routes/
 * routeRules.ts), ein direkter API-Aufruf ohne diese Berechtigung schlägt
 * mit 403 fehl.
 *
 * Speicherformat: sicheres Klammer-Markup statt rohem HTML, siehe
 * utils/routeRulesMarkup.tsx für die ausführliche Begründung (verhindert
 * XSS zuverlässig, da niemals ungeprüfter Datenbankinhalt als HTML
 * gerendert wird). Der Editor selbst ist ein unkontrolliertes
 * contentEditable-Feld (React verwaltet dessen Kindelemente NICHT, siehe
 * useEffect unten) - Fett/Unterstrichen wird über document.execCommand()
 * auf die aktuelle Textauswahl angewendet, exakt wie ein natives
 * Rich-Text-Feld.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card, CardHeader } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import { formatDateTime } from "../../utils/formatDate";
import { domToMarkup, isMarkupEmpty, parseMarkup, segmentsToReact, segmentsToSafeHtml } from "../../utils/routeRulesMarkup";
import LocationsGallery from "./LocationsGallery";
import type { RouteRules } from "../../types";

export default function RouteRulesPage() {
  const canManage = usePermission("routenregeln.manage");
  const [view, setView] = useState<"rules" | "locations">("rules");
  const [rules, setRules] = useState<RouteRules | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await api.get<RouteRules>("/api/route-rules"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Setzt den Inhalt des contentEditable-Feldes EINMALIG beim Öffnen des
  // Editors imperativ über die Ref (nicht über React-Kindelemente) - dadurch
  // "kämpft" React nicht mit den Live-DOM-Änderungen des Browsers während
  // der Bearbeitung. Der HTML-String kommt ausschließlich aus
  // segmentsToSafeHtml() (feste Erlaubnisliste b/u/br, Text escaped).
  useEffect(() => {
    if (editing && editorRef.current) {
      editorRef.current.innerHTML = segmentsToSafeHtml(parseMarkup(rules?.content ?? ""));
      editorRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  function exec(command: "bold" | "underline") {
    editorRef.current?.focus();
    document.execCommand(command);
  }

  async function handleSave() {
    if (!editorRef.current) return;
    setSaving(true);
    setError(null);
    try {
      const content = domToMarkup(editorRef.current);
      await api.put("/api/route-rules", { content });
      setEditing(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm("Möchtest du die Routenregeln wirklich vollständig löschen?")) return;
    setSaving(true);
    try {
      await api.delete("/api/route-rules");
      await load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  const lines = parseMarkup(rules?.content ?? "");
  const isEmpty = isMarkupEmpty(lines);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Routenregeln</h1>
        <p className="text-sm text-gray-500 mt-1">Verbindliche Regeln für die Benzin-Route.</p>
      </div>

      {/* "Standorte": eigener Bereich mit Bildern (siehe LocationsGallery.tsx,
          Migration 0057) - Umschalter analog zum "Offene/Bezahlte
          Sanktionen"-Muster in SanctionssystemPage.tsx. */}
      <div className="flex gap-2 border-b border-surface-border overflow-x-auto">
        <button
          onClick={() => setView("rules")}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${view === "rules" ? "border-accent text-accent" : "border-transparent text-gray-500 hover:text-gray-300"}`}
        >
          Regeltext
        </button>
        <button
          onClick={() => setView("locations")}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${view === "locations" ? "border-accent text-accent" : "border-transparent text-gray-500 hover:text-gray-300"}`}
        >
          📍 Standorte
        </button>
      </div>

      {view === "locations" ? (
        <LocationsGallery />
      ) : (
      <Card>
        <CardHeader
          title="Regeltext"
          // "updated_by_name" ist erst gesetzt, nachdem jemand tatsächlich
          // über die UI gespeichert hat (nicht schon durch die Migration,
          // die die Zeile leer anlegt) - dadurch wird "Zuletzt geändert"
          // nicht fälschlich für eine nie bearbeitete Zeile angezeigt.
          subtitle={
            rules?.updated_at && rules.updated_by_name
              ? `Zuletzt geändert: ${formatDateTime(rules.updated_at)} von ${rules.updated_by_name}`
              : undefined
          }
          action={
            canManage && !editing && (
              <div className="flex gap-2">
                <button className="btn-secondary" onClick={handleDelete} disabled={saving || isEmpty}>Löschen</button>
                <button className="btn-primary" onClick={() => { setError(null); setEditing(true); }}>Bearbeiten</button>
              </div>
            )
          }
        />

        {loading ? (
          <Spinner />
        ) : editing ? (
          <div className="space-y-3">
            {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
            <div className="flex gap-2">
              <button type="button" className="btn-secondary px-3 py-1.5 font-bold" onClick={() => exec("bold")} title="Fett">F</button>
              <button type="button" className="btn-secondary px-3 py-1.5 underline" onClick={() => exec("underline")} title="Unterstrichen">U</button>
            </div>
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              className="input min-h-[24rem] whitespace-pre-wrap leading-relaxed"
            />
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setEditing(false)} disabled={saving}>Abbrechen</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Speichern…" : "Speichern"}</button>
            </div>
          </div>
        ) : isEmpty ? (
          <p className="text-sm text-gray-500 italic">Noch keine Routenregeln hinterlegt.</p>
        ) : (
          <div className="min-h-[24rem] whitespace-pre-wrap leading-relaxed text-gray-200 text-sm">
            {segmentsToReact(lines)}
          </div>
        )}
      </Card>
      )}
    </div>
  );
}
